import React from "react";
import { IconPlus, IconTrash, IconDownload, IconFileText, IconDeviceLaptop } from "@tabler/icons-react";

export default function AppLinksManager({ links = [], onChange }) {
  const handleItemChange = (index, field, value) => {
    const updated = [...links];
    updated[index] = {
      ...updated[index],
      [field]: value
    };
    onChange(updated);
  };

  const handleAddLink = () => {
    const newLink = {
      id: `link_${Date.now()}`,
      name: "",
      download_link: "",
      manual_link: ""
    };
    onChange([...links, newLink]);
  };

  const handleRemoveLink = (index) => {
    const itemToRemove = links[index];
    const confirmMessage = itemToRemove.name 
      ? `Are you sure you want to remove "${itemToRemove.name}"?`
      : "Are you sure you want to remove this link?";
    
    if (window.confirm(confirmMessage)) {
      const updated = links.filter((_, i) => i !== index);
      onChange(updated);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-gray-100">
        <div>
          <div className="flex items-center gap-3">
            <h3 className="text-xl font-bold text-gray-800">App Download & Manual Links</h3>
            <span className="bg-green-100 text-green-700 text-xs px-2.5 py-0.5 rounded-full font-semibold">
              {links.length} {links.length === 1 ? "Link" : "Links"}
            </span>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Manage apps, software packages, and user manuals shown on the public website. Add as many custom links as needed.
          </p>
        </div>

        <button
          type="button"
          onClick={handleAddLink}
          className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-medium px-4 py-2 rounded-xl transition shadow-sm hover:shadow"
        >
          <IconPlus size={18} />
          <span>Add New App / Link</span>
        </button>
      </div>

      {links.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <IconDeviceLaptop size={48} className="mx-auto mb-2 opacity-50" />
          <p className="font-medium text-gray-600">No links added yet</p>
          <p className="text-sm text-gray-400 mt-1">Click "Add New App / Link" to create your first app or manual download field.</p>
          <button
            type="button"
            onClick={handleAddLink}
            className="mt-4 inline-flex items-center gap-2 text-green-600 hover:text-green-700 font-medium text-sm border border-green-200 hover:border-green-300 px-4 py-2 rounded-xl"
          >
            <IconPlus size={16} /> Add First Link
          </button>
        </div>
      ) : (
        <div className="space-y-4 mt-6">
          {links.map((item, index) => (
            <div
              key={item.id || index}
              className="bg-gray-50/80 hover:bg-gray-50 border border-gray-200 rounded-xl p-4 transition-all"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-gray-200 text-gray-700 text-xs flex items-center justify-center font-bold">
                    {index + 1}
                  </span>
                  <span className="font-semibold text-gray-800 text-sm">
                    {item.name ? item.name : `Link #${index + 1}`}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveLink(index)}
                  className="text-gray-400 hover:text-red-600 p-1.5 rounded-lg hover:bg-red-50 transition"
                  title="Remove this link"
                >
                  <IconTrash size={18} />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">
                    App / Resource Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-800 focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none"
                    placeholder="e.g. Captain App, Kitchen Display, Waiter POS"
                    value={item.name || ""}
                    onChange={(e) => handleItemChange(index, "name", e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1 flex items-center gap-1">
                    <IconDownload size={14} className="text-green-600" />
                    App Download Link
                  </label>
                  <input
                    type="text"
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-800 focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none"
                    placeholder="https://... (APK, EXE, or download URL)"
                    value={item.download_link || ""}
                    onChange={(e) => handleItemChange(index, "download_link", e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1 flex items-center gap-1">
                    <IconFileText size={14} className="text-blue-600" />
                    Manual Link
                  </label>
                  <input
                    type="text"
                    className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-800 focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none"
                    placeholder="https://... (PDF, guide or doc link)"
                    value={item.manual_link || ""}
                    onChange={(e) => handleItemChange(index, "manual_link", e.target.value)}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
